import * as React from 'react';

import { Button, Tab } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePowerOff, LucideTrash2 } from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';
import { TabWithWarning } from '../_shared/TabWithWarning';

import { useCancelInvitationMutation } from './_graphql/useCancelInvitationMutation';
import { useCustomerPortalUserQuery } from './_graphql/useCustomerPortalUserQuery';
import { CustomerPortalContactInfoTab } from './CustomerPortalContactInfoTab';
import { CustomerPortalUserLocationsTab } from './CustomerPortalUserLocationsTab';
import { CustomerPortalUserRolesTab } from './CustomerPortalUserRolesTab';

import { SettingsSubmodule } from '~/modules/settings/_components/_layouts/SettingsSubmodule';
import { formatDate } from '~/modules/settings/_components/_shared/utils';
import {
  $cpCurrentUser,
  $cpInvitationId,
  $cpUserDetailsTab,
  $initCpUserForm,
  type CustomerPortalUserDetailsTab,
} from '~/modules/settings/_state/customerPortalUserForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const CustomerPortalUserDetailsView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const invitationId = useAtomValue($cpInvitationId);
  const [currentTab, setCurrentTab] = useAtom($cpUserDetailsTab);
  const currentUser = useAtomValue($cpCurrentUser);
  const setCurrentUser = useSetAtom($cpCurrentUser);
  const initForm = useSetAtom($initCpUserForm);

  const { data: userData } = useCustomerPortalUserQuery({
    invitationId: invitationId ?? '',
  });

  const toast = useToast();
  const cancelMutation = useCancelInvitationMutation();

  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] =
    React.useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] =
    React.useState(false);

  React.useEffect(() => {
    if (userData?.invitation) {
      setCurrentUser(userData.invitation);
    }
  }, [userData, setCurrentUser]);

  React.useEffect(() => {
    if (currentUser) {
      initForm();
    }
  }, [currentUser, initForm]);

  const handleBack = () => {
    setLocation('/customer-portal/users');
  };

  const userDisplayName = currentUser
    ? currentUser.firstName && currentUser.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser.email
    : '';

  const handleRemoveAccessClick = () => setRemoveAccessDialogOpen(true);

  const handleRemoveAccessConfirm = async () => {
    if (!invitationId) return;
    try {
      await cancelMutation.mutateAsync({ id: invitationId });
      setRemoveAccessDialogOpen(false);
      toast.success(t.customerPortal.userDetails.messages.accessRemoved);
    } catch {
      toast.error(t.customerPortal.userDetails.messages.accessRemoveError);
    }
  };

  const handleDeleteAccountClick = () => setDeleteAccountDialogOpen(true);

  const handleDeleteAccountConfirm = async () => {
    if (!invitationId) return;
    try {
      await cancelMutation.mutateAsync({ id: invitationId });
      setDeleteAccountDialogOpen(false);
      toast.success(t.customerPortal.userDetails.messages.accountDeleted);
      setLocation('/customer-portal/users');
    } catch {
      toast.error(t.customerPortal.userDetails.messages.accountDeleteError);
    }
  };

  const handleRemoveAccessDialogOpenChange = (open: boolean) => {
    if (!cancelMutation.isPending) setRemoveAccessDialogOpen(open);
  };

  const handleDeleteAccountDialogOpenChange = (open: boolean) => {
    if (!cancelMutation.isPending) setDeleteAccountDialogOpen(open);
  };

  const isActionPending = cancelMutation.isPending;

  const handleTabChange = (tab: CustomerPortalUserDetailsTab) => {
    setCurrentTab(tab);
  };

  const titleDisplay = currentUser ? (
    currentUser.firstName && currentUser.lastName ? (
      <>
        <span className="font-bold">
          {currentUser.firstName} {currentUser.lastName}
        </span>
        {currentUser.company && (
          <span className="font-normal text-neutral/60">
            {' '}
            · {currentUser.company}
          </span>
        )}
      </>
    ) : (
      currentUser.email
    )
  ) : (
    'Loading...'
  );

  const subtitle = currentUser
    ? t.customerPortal.userDetails.activeSince(
        formatDate(currentUser.createdAt),
      )
    : 'Loading...';

  return (
    <SettingsSubmodule
      className="@CustomerPortalUserDetailsView h-full"
      contentClassName="p-0"
      title={titleDisplay}
      subtitle={subtitle}
      onBackPressed={handleBack}
      tools={
        <>
          <Button
            variant="outline"
            icon={<LucidePowerOff className="size-4" />}
            onPress={handleRemoveAccessClick}
            isDisabled={!currentUser || isActionPending}
          >
            {t.customerPortal.actions.removeAccess}
          </Button>
          <Button
            variant="outline"
            color="failure"
            icon={<LucideTrash2 className="size-4" />}
            onPress={handleDeleteAccountClick}
            isDisabled={!currentUser || isActionPending}
          >
            {t.customerPortal.actions.deleteAccount}
          </Button>
        </>
      }
      tabs={
        <>
          <Tab
            selected={currentTab === 'contact-info'}
            onClick={() => handleTabChange('contact-info')}
          >
            {t.customerPortal.userDetails.tabs.contactInfo}
          </Tab>
          <TabWithWarning
            label={t.customerPortal.userDetails.tabs.roles}
            selected={currentTab === 'roles'}
            onClick={() => handleTabChange('roles')}
          />
          <TabWithWarning
            label={t.customerPortal.userDetails.tabs.locations}
            selected={currentTab === 'locations'}
            onClick={() => handleTabChange('locations')}
          />
        </>
      }
    >
      {currentTab === 'contact-info' && <CustomerPortalContactInfoTab />}
      {currentTab === 'roles' && <CustomerPortalUserRolesTab />}
      {currentTab === 'locations' && <CustomerPortalUserLocationsTab />}

      <ConfirmationDialog
        isOpen={removeAccessDialogOpen}
        onOpenChange={handleRemoveAccessDialogOpenChange}
        title={t.customerPortal.users.dialogs.removeAccess.title(
          userDisplayName,
        )}
        description={t.customerPortal.users.dialogs.removeAccess.description(
          userDisplayName,
        )}
        confirmLabel={t.customerPortal.users.dialogs.removeAccess.remove}
        cancelLabel={t.customerPortal.users.dialogs.removeAccess.discard}
        onConfirm={handleRemoveAccessConfirm}
        onCancel={() => handleRemoveAccessDialogOpenChange(false)}
        isLoading={isActionPending}
      />

      <ConfirmationDialog
        isOpen={deleteAccountDialogOpen}
        onOpenChange={handleDeleteAccountDialogOpenChange}
        title={t.customerPortal.users.dialogs.deleteAccount.title(
          userDisplayName,
        )}
        description={t.customerPortal.users.dialogs.deleteAccount.description(
          userDisplayName,
        )}
        confirmLabel={t.customerPortal.users.dialogs.deleteAccount.delete}
        cancelLabel={t.customerPortal.users.dialogs.deleteAccount.discard}
        onConfirm={handleDeleteAccountConfirm}
        onCancel={() => handleDeleteAccountDialogOpenChange(false)}
        isLoading={isActionPending}
      />
    </SettingsSubmodule>
  );
};
